/** Claims we read from Okta ID tokens / Stealth JWTs for session UI (not verification). */
export interface JwtIdentityClaims {
  email?: string;
  displayName?: string;
  roleClaims: string[];
}

/**
 * Decodes a JWT payload without verifying the signature.
 * Used only to seed session email/name after a trusted STS exchange.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length < 2) {
    return null;
  }
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = atob(padded);
    const parsed: unknown = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export function identityClaimsFromJwt(token: string): JwtIdentityClaims {
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return { roleClaims: [] };
  }

  const email =
    readStringClaim(payload, 'email') ??
    readStringClaim(payload, 'preferred_username') ??
    readStringClaim(payload, 'upn');

  const displayName =
    readStringClaim(payload, 'name') ??
    readStringClaim(payload, 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name');

  const roleClaims = ROLE_CLAIM_KEYS.flatMap((key) => readStringClaims(payload, key));

  return { email, displayName, roleClaims };
}

/** JWT `exp` (unix seconds) as epoch ms. Missing or non-positive values are ignored. */
export function readJwtExpiryMs(token: string): number | null {
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return null;
  }
  const exp = payload['exp'];
  if (typeof exp !== 'number' || !Number.isFinite(exp) || exp <= 0) {
    return null;
  }
  return exp * 1000;
}

/** Claim keys that have carried a role on Okta or Stealth tokens. */
export const ROLE_CLAIM_KEYS = [
  'role',
  'roles',
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role',
] as const;

function readStringClaim(payload: Record<string, unknown>, key: string): string | undefined {
  const value = payload[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function readStringClaims(payload: Record<string, unknown>, key: string): string[] {
  const value = payload[key];
  if (typeof value === 'string') {
    const claim = value.trim();
    return claim ? [claim] : [];
  }
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((entry) =>
    typeof entry === 'string' && entry.trim() ? [entry.trim()] : [],
  );
}
